/**
 * QRScan -- mobile QR code scanner page.
 *
 * Engineers scan a Thread-tagged plot in a meeting and see it on their phone
 * with full provenance context. After detecting a valid QR code, the page
 * fetches artifact metadata from `/api/provenance/a/:id/info` and then
 * auto-redirects to the provenance view after a brief delay.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Card,
  Callout,
  Heading,
  Separator,
  Text,
} from "@radix-ui/themes";

import { Scanner, type ScanResult } from "../components/qr/Scanner";
import { CheckIcon, WarningIcon } from "../components/qr/icons";

interface ArtifactInfo {
  artifact_id: string;
  project_id?: string;
  project_name?: string;
  is_public?: boolean;
  asset_url?: string;
  exists: boolean;
}

export default function QRScan() {
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [artifactInfo, setArtifactInfo] = useState<ArtifactInfo | null>(null);
  const [fetchError, setFetchError] = useState("");
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(
    null,
  );
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch artifact info after QR detection
  useEffect(() => {
    if (!scanResult) return;

    const controller = new AbortController();

    async function fetchInfo() {
      try {
        const res = await fetch(
          `/api/provenance/a/${scanResult!.artifactId}/info`,
          { signal: controller.signal },
        );
        if (!res.ok) {
          setFetchError(`Server returned ${res.status}`);
          return;
        }
        const data: ArtifactInfo = await res.json();
        setArtifactInfo(data);

        if (data.exists) {
          // Start 2-second countdown then redirect via the backend redirect
          setRedirectCountdown(2);
        }
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setFetchError(err.message || "Network error");
        }
      }
    }

    fetchInfo();
    return () => controller.abort();
  }, [scanResult]);

  // Auto-redirect countdown
  useEffect(() => {
    if (redirectCountdown === null || !scanResult) return;

    if (redirectCountdown <= 0) {
      // Redirect through the backend route which handles auth-aware redirects
      window.location.href = `/api/provenance/a/${scanResult.artifactId}`;
      return;
    }

    countdownRef.current = setInterval(() => {
      setRedirectCountdown((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [redirectCountdown, scanResult]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const handleDetected = useCallback((result: ScanResult) => {
    setScanResult(result);
    setFetchError("");
    setArtifactInfo(null);
  }, []);

  const handleScanError = useCallback((error: string) => {
    setFetchError(error);
  }, []);

  const handleRedirectNow = useCallback(() => {
    if (scanResult) {
      window.location.href = `/api/provenance/a/${scanResult.artifactId}`;
    }
  }, [scanResult]);

  const handleReset = useCallback(() => {
    setScanResult(null);
    setArtifactInfo(null);
    setFetchError("");
    setRedirectCountdown(null);
  }, []);

  return (
    <Box
      p="4"
      style={{
        maxWidth: 480,
        margin: "0 auto",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <Box>
        <Heading size="5" mb="2">
          Scan Plot QR Code
        </Heading>
        <Text size="2" color="gray">
          Point your camera at a Thread-tagged plot to see its provenance.
        </Text>
      </Box>

      <Separator size="4" />

      {/* Scanner -- only show when we haven't detected anything yet */}
      {!scanResult && (
        <Scanner onDetected={handleDetected} onError={handleScanError} />
      )}

      {/* Artifact info card */}
      {scanResult && (
        <Card>
          <Box style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Loading state */}
            {!artifactInfo && !fetchError && (
              <Text size="2" color="gray">
                Looking up artifact...
              </Text>
            )}

            {/* Fetch error */}
            {fetchError && (
              <Callout.Root color="red" size="1">
                <Callout.Icon>
                  <WarningIcon />
                </Callout.Icon>
                <Callout.Text>{fetchError}</Callout.Text>
              </Callout.Root>
            )}

            {/* Artifact found */}
            {artifactInfo?.exists && (
              <>
                <Box
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <CheckIcon size={20} color="var(--green-11)" />
                  <Text size="3" weight="medium">
                    Thread Artifact Found
                  </Text>
                </Box>

                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <Text size="2" color="gray">
                    Project:{" "}
                    <Text weight="medium" color="gray" highContrast>
                      {artifactInfo.project_name}
                    </Text>
                  </Text>
                  <Text size="1" color="gray">
                    ID: {artifactInfo.artifact_id}
                  </Text>
                  {artifactInfo.is_public && (
                    <Text size="1" color="blue">
                      Public project
                    </Text>
                  )}
                </Box>

                {redirectCountdown !== null && redirectCountdown > 0 && (
                  <Text
                    size="2"
                    color="blue"
                    align="center"
                    style={{ cursor: "pointer" }}
                    onClick={handleRedirectNow}
                  >
                    Opening provenance view in {redirectCountdown}s... (tap to
                    go now)
                  </Text>
                )}
              </>
            )}

            {/* Artifact not found */}
            {artifactInfo && !artifactInfo.exists && (
              <Callout.Root color="orange" size="1">
                <Callout.Icon>
                  <WarningIcon />
                </Callout.Icon>
                <Callout.Text>
                  This artifact was not found. It may have been deleted or the
                  QR code may be outdated.
                </Callout.Text>
              </Callout.Root>
            )}

            {/* Reset button */}
            <Text
              size="2"
              color="gray"
              align="center"
              style={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={handleReset}
            >
              Scan another code
            </Text>
          </Box>
        </Card>
      )}
    </Box>
  );
}
